import React, { useState, useEffect, useRef } from 'react'
import withSSR from 'js/components/withSSR/withSSR'
import Spinner from 'js/components/Spinner/Spinner'
import ErrorPage from 'js/pages/ErrorPage'
import { runQuery } from 'js/dato'
import Footer from 'js/components/Footer/Footer'
import { Navigation } from 'js/pages/Home/Navigation'
import Television from './assets/television.svg?svgr-webpack'
import IconYoutube from './assets/icon-youtube.svg?svgr-webpack'
import SymbolHeading from './components/SymbolHeading'
import VideoThumb from './components/VideoThumb'
import VideoPlayer from './components/VideoPlayer'
import HashAnchor from 'js/components/HashAnchor/HashAnchor'
import { Title } from 'react-head'
import {
  NutritionTimes,
  NutritionTimes_videos_video,
} from './types/NutritionTimes'
import { useNavigate } from 'react-router-dom'
import { NonNullableObject } from 'typings'
import LSButton from 'js/components/LSButton'

type Props = {
  data: NutritionTimes & { selectedVideo: string | undefined }
  isLoading: boolean
  error: any
}

function NutritionVideos({ data, isLoading, error }: Props) {
  const [selectedVideo, setSelectedVideo] = useState(data?.videos[0])
  const videoPlayerRef = useRef<HTMLDivElement>(null!)
  const navigate = useNavigate()

  useEffect(() => {
    if (data)
      setSelectedVideo(
        data.videos[data.selectedVideo ? Number(data.selectedVideo) - 1 : 0],
      )
  }, [data])

  function selectVideo(video) {
    if (video !== selectedVideo) {
      setSelectedVideo(video)
      navigate(`/nutrition-explained/video/${video.episodeNumber}`)
    }

    if (videoPlayerRef.current)
      videoPlayerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
  }

  if (isLoading || !selectedVideo)
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner width="50" color="green" />
      </div>
    )

  if (error) {
    return <ErrorPage error={error} />
  }

  const { videos } = data

  return (
    <>
      <Title>Nutrition videos - Lifesum</Title>
      <div className="bg-BgSub py-20 lg:py-40">
        <Navigation theme="sub" minimal={false} />
      </div>
      <h1 className="sr-only">Videos</h1>
      <div
        className="bg-BgSub px-16 pb-80 pt-16 md:px-24 md:pt-24 lg:pb-128 lg:pt-56"
        ref={videoPlayerRef}
      >
        <div className="mx-auto max-w-768 xl:max-w-968">
          <VideoPlayer
            {...(selectedVideo?.video as NonNullableObject<NutritionTimes_videos_video>)}
            name={selectedVideo.name}
            subheading={`Episode ${selectedVideo.episodeNumber}`}
            description={selectedVideo.description}
          />
        </div>
      </div>
      <div className="bg-Bg pb-128">
        <HashAnchor id="episodes">
          <div className="-mt-40 pb-12">
            <SymbolHeading symbol={Television} symbolStyle={{ marginTop: -1 }}>
              Episodes
            </SymbolHeading>
          </div>
        </HashAnchor>
        <div className="o-width-limiter">
          <div className="-mt-8 mb-40 lg:-mt-16">
            <div className="grid gap-1 md:grid-cols-2 md:gap-3 lg:grid-cols-3 lg:gap-4 xl:grid-cols-4">
              {videos.map(({ video, name, episodeNumber, description }) => {
                if (!video) {
                  throw new Error(
                    `NutritionVideos missing required video prop: name - ${name}, episodeNumber: ${episodeNumber}`,
                  )
                }

                return (
                  <VideoThumb
                    video={video}
                    key={video.id}
                    selectVideo={() =>
                      selectVideo({ video, episodeNumber, description, name })
                    }
                    isSelected={selectedVideo?.video?.id === video.id}
                    name={name}
                    subheading={`Episode ${episodeNumber}`}
                  />
                )
              })}
            </div>
          </div>
          <SymbolHeading symbol={IconYoutube}>More videos</SymbolHeading>
          <div className="mx-auto max-w-768 text-center">
            <p className="mb-32 leading-body lg:mb-48">
              If you enjoyed this, make sure to check out more videos over at
              our YouTube channel — and don’t forget to subscribe for a new
              video every week!
            </p>
            <LSButton
              variant="secondary"
              link={true}
              size="small"
              href="https://www.youtube.com/channel/UCy64gY3TCURHxigExH0cHIg"
            >
              See more videos
            </LSButton>
          </div>
        </div>
      </div>
      <Footer marginless />
    </>
  )
}

const query = gql`
  query NutritionTimes {
    videos: allNutritionTimes(first: 50, orderBy: episodeNumber_ASC) {
      episodeNumber
      name
      description
      video {
        id: providerUid
        thumbnailUrl
        url
        width
        height
      }
    }
  }
`

NutritionVideos.getInitialProps = ({ params }) => {
  return runQuery(query).then((data) => {
    return {
      ...data,
      selectedVideo: params.video,
    }
  })
}

export default withSSR(NutritionVideos)
